// Explore › Traces (APS-198, part C; design "Explore"): the project's spans from a few words
// (lib/spanQuery.ts), their count and failures per bucket with the median and 95th percentile
// durations, and the spans newest or slowest first, each opening its trace in the trace view. The
// API builds the SQL (POST /explore/traces) and runs it as the project's reader on every plan; its
// SQL can be read here and opened in the SQL tab. The query, the range and the order live in the
// address, so a search can be shared. Spans are the app's data: shown as text, never translated.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { exploreFieldsQuery, fetchExploreTraces } from '../api/queries'
import type { ExploreTracesQuery } from '../api/types'
import { DebugOnlyLine } from '../components/DebugOnly'
import { Bars, EmptyNote, ScrollX, Section } from '../components/d'
import { LineChart } from '../components/LineChart'
import { Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { compact, duration, durationAxis } from '../lib/format'
import {
  formatSpanQuery,
  parseSpanQuery,
  SPAN_KINDS,
  SPAN_STATUSES,
  type SpanQueryError,
} from '../lib/spanQuery'
import { saveDraft } from './Explore'
import { queryErrorText, RANGE_MS, runError } from './exploreQuery'
import { useProjectParams } from './params'
import {
  EXPLORE_RANGES,
  type ExploreRange,
  type ExploreSearch,
  SPAN_SORTS,
  type SpanSort,
} from './search'

export function spanQueryErrorText(t: Translator['t'], error: SpanQueryError): string {
  if (error.code === 'duration')
    return t('explore.traces.err.duration', { value: error.value, position: error.position })
  if (error.code === 'is')
    return t('explore.traces.err.is', { value: error.value, position: error.position })
  return queryErrorText(t, error)
}

export function ExploreTraces() {
  const i18n = useI18n()
  const { t, number, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ExploreSearch
  const navigate = useNavigate()
  const range = search.range ?? '24h'
  const sort = search.sort ?? 'recent'
  const [text, setText] = useState(search.q ?? '')
  useEffect(() => setText(search.q ?? ''), [search.q])
  const ids = { q: useId(), range: useId(), sort: useId(), hint: useId(), error: useId() }

  const parsed = parseSpanQuery(search.q ?? '')
  const typed = parseSpanQuery(text)
  // The run is the address's: the same words, range and order read the same spans.
  const [now] = useState(() => Math.floor(Date.now() / 60_000) * 60_000)
  const body: ExploreTracesQuery | undefined =
    'query' in parsed
      ? {
          from: new Date(now - RANGE_MS[range]).toISOString(),
          to: new Date(now + 60_000).toISOString(),
          ...(search.app ? { app_id: search.app } : {}),
          ...(search.build ? { build: search.build } : {}),
          filters: parsed.query.filters,
          errors_only: !!parsed.query.failed,
          root_only: !!parsed.query.root,
          ...(parsed.query.minMs !== undefined ? { min_duration_ms: parsed.query.minMs } : {}),
          ...(parsed.query.maxMs !== undefined ? { max_duration_ms: parsed.query.maxMs } : {}),
          sort,
          limit: 100,
        }
      : undefined
  const traces = useInfiniteQuery({
    queryKey: ['project', projectId, 'explore-traces', body],
    queryFn: ({ pageParam }) =>
      fetchExploreTraces(projectId, body as ExploreTracesQuery, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!body,
    retry: false,
  })
  const fields = useQuery(exploreFieldsQuery(projectId, search.app, 'spans'))

  function run(words: string, next: { range?: ExploreRange; sort?: SpanSort } = {}) {
    const nextRange = next.range ?? range
    const nextSort = next.sort ?? sort
    navigate({
      to: '.',
      search: {
        ...(search.app ? { app: search.app } : {}),
        tab: 'traces',
        ...(words.trim() ? { q: words.trim() } : {}),
        ...(nextRange !== '24h' ? { range: nextRange } : {}),
        ...(nextSort !== 'recent' ? { sort: nextSort } : {}),
      },
    })
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if ('error' in typed) return
    run(text)
  }

  /** Adds a filter from a suggestion, as words, and runs it. */
  function add(field: string, value?: string) {
    const base = 'query' in typed ? typed.query : { filters: [] }
    const words = formatSpanQuery({
      ...base,
      filters: [
        ...base.filters,
        value === undefined
          ? { field, op: 'exists' as const }
          : { field, op: 'eq' as const, value },
      ],
    })
    setText(words)
    run(words)
  }

  const first = traces.data?.pages[0]
  const spans = traces.data?.pages.flatMap((p) => p.spans) ?? []
  const histogram = first?.histogram ?? []
  const labels = histogram.map((b) =>
    (first?.bucket_seconds ?? 0) >= 86_400
      ? date(b.t, { month: 'short', day: 'numeric' })
      : date(b.t, { hour: '2-digit', minute: '2-digit' }),
  )

  return (
    <div className="cols">
      <div className="col-main">
        <Section title={t('explore.traces.query')}>
          <form className="form pb" onSubmit={submit}>
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <div className="field" style={{ flex: '1 1 320px' }}>
                <label htmlFor={ids.q}>{t('explore.logs.words')}</label>
                <input
                  id={ids.q}
                  type="search"
                  className="input mono"
                  style={{ maxWidth: 'none' }}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  translate="no"
                  maxLength={1000}
                  placeholder="app.start is:failed duration>=250ms"
                  aria-describedby={'error' in typed ? ids.error : ids.hint}
                  aria-invalid={'error' in typed || undefined}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor={ids.range}>{t('explore.logs.range')}</label>
                <select
                  id={ids.range}
                  className="input"
                  value={range}
                  onChange={(e) => run(text, { range: e.target.value as ExploreRange })}
                >
                  {EXPLORE_RANGES.map((r) => (
                    <option key={r} value={r}>
                      {t(`explore.logs.range.${r}` as MessageId)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor={ids.sort}>{t('explore.traces.sort')}</label>
                <select
                  id={ids.sort}
                  className="input"
                  value={sort}
                  onChange={(e) => run(text, { sort: e.target.value as SpanSort })}
                >
                  {SPAN_SORTS.map((s) => (
                    <option key={s} value={s}>
                      {t(`explore.traces.sort.${s}` as MessageId)}
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn solid" disabled={'error' in typed}>
                {t('explore.run')}
              </button>
            </div>
            {'error' in typed ? (
              <p id={ids.error} role="alert" className="notice error">
                {spanQueryErrorText(t, typed.error)}
              </p>
            ) : (
              <span id={ids.hint} className="hint">
                {t('explore.traces.hint')}
              </span>
            )}
          </form>
        </Section>

        <Section
          title={t('explore.traces.volume')}
          meta={first ? t('explore.logs.volumeSub', { seconds: first.bucket_seconds }) : undefined}
        >
          {traces.isPending ? (
            <Loading />
          ) : traces.isError ? (
            <div className="pb">
              <p role="alert" className="notice error">
                {runError(t, traces.error)}
              </p>
            </div>
          ) : histogram.length === 0 ? (
            <EmptyNote title={t('explore.traces.none')}>
              {t('explore.traces.noneBody')}
              <DebugOnlyLine
                projectId={projectId}
                scope={{ app: search.app, days: Math.max(1, RANGE_MS[range] / 86_400_000) }}
                empty
              />
            </EmptyNote>
          ) : (
            <div className="pb">
              <Bars
                categories={labels}
                series={[
                  { name: t('explore.traces.spans'), values: histogram.map((b) => b.spans) },
                  { name: t('explore.traces.failed'), values: histogram.map((b) => b.failed) },
                ]}
                format={(v) => compact(i18n, v)}
                label={t('explore.traces.volumeLabel')}
                height={140}
              />
              <LineChart
                label={t('explore.traces.durationLabel')}
                name={t('explore.traces.p95')}
                points={histogram.map((b, i) => ({ label: labels[i] ?? '', value: b.p95_ms }))}
                more={[{ name: t('explore.traces.p50'), values: histogram.map((b) => b.p50_ms) }]}
                // One unit on the axis, even steps from 0 (APS-408); the full duration on the line.
                fromZero
                format={durationAxis(
                  i18n,
                  Math.max(0, ...histogram.flatMap((b) => [b.p95_ms ?? 0, b.p50_ms ?? 0])),
                )}
                lastLabel={(p) => (p.value === null ? '' : duration(i18n, p.value))}
                height={140}
              />
            </div>
          )}
        </Section>

        <Section
          title={t('explore.traces.spansTitle')}
          meta={t('explore.traces.spansSub')}
          right={
            first ? (
              <button
                type="button"
                className="btn small"
                onClick={() => {
                  saveDraft(projectId, first.sql)
                  navigate({
                    to: '.',
                    search: search.app ? { app: search.app } : {},
                  })
                }}
              >
                {t('explore.logs.openSql')}
              </button>
            ) : undefined
          }
        >
          {traces.isPending ? (
            <Loading />
          ) : traces.isError ? null : spans.length === 0 ? (
            <EmptyNote title={t('explore.traces.none')} />
          ) : (
            <>
              <ScrollX label={t('explore.traces.spansTable')}>
                <table className="t">
                  <thead>
                    <tr>
                      <th scope="col">{t('explore.traces.col.span')}</th>
                      <th scope="col">{t('explore.traces.col.kind')}</th>
                      <th scope="col" className="num">
                        {t('explore.traces.col.duration')}
                      </th>
                      <th scope="col">{t('explore.traces.col.status')}</th>
                      <th scope="col">{t('explore.logs.col.session')}</th>
                      <th scope="col">{t('explore.logs.col.release')}</th>
                      <th scope="col">{t('explore.logs.col.time')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spans.map((s) => (
                      <tr key={`${s.trace_id}:${s.span_id}`}>
                        <td>
                          <Link
                            to="/o/$orgId/p/$projectId/traces/$traceId"
                            params={{ orgId, projectId, traceId: s.trace_id }}
                            search={search.app ? { app: search.app } : {}}
                            className="mono"
                            translate="no"
                          >
                            {s.name || t('explore.traces.noName')}
                          </Link>
                        </td>
                        {/* Lowercase as D tables show it; the filters keep the exact token. */}
                        <td className="cell-mono" translate="no">
                          {s.kind.toLowerCase()}
                        </td>
                        <td className="num">{duration(i18n, s.duration_ms)}</td>
                        <td>
                          {s.failed ? (
                            <span className="pill crash" title={s.status_message || undefined}>
                              {s.http_status
                                ? t('explore.traces.failedStatus', { status: s.http_status })
                                : t('explore.traces.failed')}
                            </span>
                          ) : s.http_status ? (
                            <span className="pill neutral">{number(s.http_status)}</span>
                          ) : (
                            <span style={{ color: 'var(--ink-3-text)' }} translate="no">
                              {s.status_code === 'OK' ? (
                                'ok'
                              ) : (
                                <>
                                  <span aria-hidden="true">—</span>
                                  <span className="visually-hidden">
                                    {t('explore.traces.noStatus')}
                                  </span>
                                </>
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          {s.session_id ? (
                            <Link
                              to="/o/$orgId/p/$projectId/sessions/$sessionId"
                              params={{ orgId, projectId, sessionId: s.session_id }}
                              search={search.app ? { app: search.app } : {}}
                              className="cell-mono"
                              translate="no"
                            >
                              {s.session_id.slice(0, 8)}
                            </Link>
                          ) : null}
                        </td>
                        <td className="cell-mono" translate="no">
                          {s.release}
                        </td>
                        <td className="cell-mono">
                          {date(s.start, { dateStyle: 'short', timeStyle: 'medium' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
              {traces.hasNextPage ? (
                <div className="pb">
                  <button
                    type="button"
                    className="btn small"
                    disabled={traces.isFetchingNextPage}
                    onClick={() => traces.fetchNextPage()}
                  >
                    {t('explore.logs.more')}
                  </button>
                </div>
              ) : null}
            </>
          )}
          {first ? (
            <details className="pb">
              <summary>{t('explore.logs.sql')}</summary>
              <div className="code" translate="no">
                <pre style={{ whiteSpace: 'pre-wrap' }}>{first.sql}</pre>
              </div>
            </details>
          ) : null}
        </Section>
      </div>

      <div className="col-side">
        <Section title={t('explore.traces.kinds')} meta={t('explore.traces.kindsSub')}>
          <ul className="ruled-list">
            {(
              [
                ['span.kind', 'explore.traces.col.kind', SPAN_KINDS],
                ['span.status_code', 'explore.traces.col.status', SPAN_STATUSES],
              ] as const
            ).map(([field, label, values]) => (
              // The label above its values in both rows, the values wrapping under it.
              <li key={field} style={{ flexDirection: 'column', gap: 6 }}>
                <span className="dim">{t(label)}</span>
                <div className="head-actions" style={{ gap: 8 }}>
                  {values.map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="link-btn mono"
                      translate="no"
                      style={{ fontSize: 12 }}
                      onClick={() => add(field, v)}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </Section>
        <Section title={t('explore.logs.fields')} meta={t('explore.traces.fieldsSub')}>
          {fields.isPending ? (
            <Loading />
          ) : fields.isError || !fields.data || fields.data.attributes.length === 0 ? (
            <EmptyNote title={t('explore.logs.noFields')} />
          ) : (
            <ul className="ruled-list">
              {fields.data.attributes.slice(0, 30).map((f) => (
                <li key={f.key}>
                  <button
                    type="button"
                    className="link-btn mono"
                    translate="no"
                    onClick={() => add(f.key)}
                  >
                    {f.key}
                  </button>{' '}
                  <span className="dim">{number(f.records)}</span>
                  {f.values.length ? (
                    <div className="head-actions" style={{ gap: 8 }}>
                      {f.values.slice(0, 5).map((v) => (
                        <button
                          key={v}
                          type="button"
                          className="link-btn mono"
                          translate="no"
                          style={{ fontSize: 12 }}
                          onClick={() => add(f.key, v)}
                        >
                          {v.length > 40 ? `${v.slice(0, 40)}…` : v}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  )
}
